/**
 * Footer Links (Solid.js) – Link-Spalten, Brand, Social, Copyright
 * Styles in global.css: .footer-links-*
 * Social-Media-Links werden von /api/social-media geladen (Admin-Bereich).
 */
import { A } from '@solidjs/router';
import { createResource, createSignal, onMount, Show, For } from 'solid-js';
import { useAuth } from '../context/AuthContext';
import axios from '../config/axios';
import { useTranslation } from '../context/LanguageContext';

interface FooterLinksProps {
  isOpen?: boolean;
}

interface SocialLink {
  id: string;
  name: string;
  url: string;
  icon: string;
  enabled: boolean;
}

/** Icon-String aus Backend (z. B. "fab fa-discord") in FA-Klassen umwandeln (fa-brands fa-discord). */
function iconToClass(icon: string): string {
  if (!icon || typeof icon !== 'string') return 'fa-solid fa-link';
  const s = icon.trim();
  if (s.startsWith('fa-')) return s; // bereits fa-brands/fa-solid/fa-regular
  const pref = s.split(' ')[0];
  const rest = s.slice(pref.length).trim();
  const map: Record<string, string> = { fab: 'fa-brands', fas: 'fa-solid', far: 'fa-regular' };
  return `${map[pref] || 'fa-solid'} ${rest}`.trim();
}

async function fetchSocialMedia(): Promise<SocialLink[]> {
  try {
    const res = await axios.get<{ success?: boolean; data?: { links?: SocialLink[] } }>('/api/social-media');
    const links = res.data?.data?.links ?? [];
    return Array.isArray(links) ? links.filter((l) => l.enabled) : [];
  } catch {
    return [];
  }
}

export default function FooterLinks(props: FooterLinksProps) {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const auth = useAuth();
  const [socialData] = createResource(fetchSocialMedia);
  const [version, setVersion] = createSignal('');
  onMount(() => {
    axios.get<{ success: boolean; data?: { version: string } }>('/api/app-version')
      .then((res) => { if (res.data?.success && res.data.data?.version) setVersion(res.data.data.version); })
      .catch(() => {});
  });

  return (
    <div class={`footer-links-collapse-wrap${props.isOpen === true ? ' footer-links-collapse-wrap--open' : ''}`}>
    <footer class="footer-links" role="contentinfo">
      <div class="footer-links-accent" aria-hidden />
      <div class="footer-links-inner">
        <div class="footer-links-grid">
          <div class="footer-links-brand">
            <div class="footer-links-brand-logo">
              <span class="footer-links-brand-logo-icon" aria-hidden>🐜</span>
              <span>King of Ants</span>
              <Show when={version()}><span class="footer-links-version">{version()}</span></Show>
            </div>
            <p class="footer-links-brand-tagline">
              {t('footer.tagline')}
            </p>
            <div class="footer-links-social-icons">
            <Show when={!socialData.loading && socialData()?.length} fallback={null}>
              <For each={socialData()}>
                {(link) => (
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={link.name}
                  >
                    <i class={iconToClass(link.icon)} aria-hidden />
                  </a>
                )}
              </For>
            </Show>
          </div>
          </div>

          <div class="footer-links-column">
            <h4 class="footer-links-column-title">{t('footer.overview')}</h4>
            <ul class="footer-links-list">
              <li><A href="/tutorial">📖 {t('footer.tutorial')}</A></li>
              <li><A href={auth.isAuthenticated ? '/lexicon' : '/arten'}>📚 {t('footer.lexicon')}</A></li>
              <li><A href="/rangliste">🏆 {t('footer.leaderboard')}</A></li>
              <li><A href="/statistics">📊 {t('footer.statistics')}</A></li>
              <li><A href="/news">📰 {t('footer.news')}</A></li>
            </ul>
          </div>

          <div class="footer-links-column">
            <h4 class="footer-links-column-title">{t('footer.information')}</h4>
            <ul class="footer-links-list">
              <li><A href="/hilfe">❓ {t('footer.help')}</A></li>
              <li><A href="/support">🎫 {t('footer.support')}</A></li>
              <li><A href="/bug-report">🐛 {t('footer.bugReport')}</A></li>
              <li><A href="/partner-shops">🤝 {t('footer.partnerShops')}</A></li>
              <li><A href="/credits">👥 {t('footer.credits')}</A></li>
              <li><A href="/links">🔗 {t('footer.links')}</A></li>
            </ul>
          </div>

          <div class="footer-links-column">
            <h4 class="footer-links-column-title">{t('footer.legal')}</h4>
            <ul class="footer-links-list">
              <li><A href="/impressum">📄 {t('footer.impressum')}</A></li>
              <li><A href="/datenschutz">🔒 {t('footer.datenschutz')}</A></li>
              <li><A href="/agb">📋 {t('footer.agb')}</A></li>
              <li><A href="/cookie-settings">🍪 {t('footer.cookieSettings')}</A></li>
            </ul>
          </div>

        </div>

        <div class="footer-links-bottom">
          <p class="footer-links-copyright">
            © {currentYear} King of Ants. {t('footer.allRightsReserved')}
          </p>
          <p class="footer-links-disclaimer">
            {t('footer.nonCommercialDisclaimer')}
          </p>
        </div>
      </div>
    </footer>
    </div>
  );
}
